"use client";
import "./funds_concept.scss";
import Link from "next/link";
import { Container } from "@/elements/container/container";
import { Stack } from "@/elements/stack/stack";
import { Text } from "@/elements/text/text";
import { i18n } from "@/model/i18n";
import { type ReactElement } from "react";
import { Button } from "@/elements/button/button";
import { useFunds } from "@/contexts/funds-context";
import { PerformanceValue } from "@/components/performance_value/performance_value";
import { Formatter } from "../../../model/formatter";

export function FundsConcept(): ReactElement | null {
  const { selectedFund, selectedTranche } = useFunds();
  const formatter = new Formatter({ locale: "de" });

  return (
    <section className="funds-concept" aria-label="funds-concept">
      <Container>
        <Stack size={60} desktopSize={80}>
          <section
            className="funds-concept__section"
            aria-label="funds-concept-top"
          >
            <Text
              type="sh1-xbold"
              htmlElement="h2"
              color="darker-pairings-primary"
            >
              {i18n.t("pages.funds.concept.title")}
            </Text>
            <Stack size={24} tabletSize={40}>
              <Text
                type="sh1-regular"
                color="neutral-on-surface"
                htmlElement="p"
              >
                {formatter.parseText(selectedFund?.conceptText || "")}
              </Text>
              <Link href="/expertise/advisor">
                <Button
                  title={i18n.t(
                    "pages.funds.concept.more_about_our_investment_process",
                  )}
                ></Button>
              </Link>
            </Stack>
          </section>
          <section
            className="funds-concept__section"
            aria-label="funds-concept-bottom"
          >
            <Text
              type="sh1-xbold"
              htmlElement="h2"
              color="darker-pairings-primary"
            >
              {i18n.t("pages.funds.fund_key_figures.title")}
            </Text>
            <ul className="funds-concept__grid">
              <li className="funds-concept__item funds-concept__item--legal_form">
                <PerformanceValue
                  label={i18n.t("pages.funds.fund_key_figures.legal_form")}
                  value={selectedFund?.legalForm}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--launched-at">
                <PerformanceValue
                  label={i18n.t("pages.funds.fund_key_figures.launched_at")}
                  value={selectedTranche?.launchedAt}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--fiscal_year_end">
                <PerformanceValue
                  label={i18n.t("pages.funds.fund_key_figures.fiscal_year_end")}
                  value={selectedFund?.financialYearEndAt}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--as_of_date">
                <PerformanceValue
                  label={i18n.t("pages.funds.fund_key_figures.as_of_date")}
                  value={selectedTranche?.modifiedAt}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--last_distribution">
                <PerformanceValue
                  label={i18n.t(
                    "pages.funds.fund_key_figures.last_distribution",
                  )}
                  value={selectedFund?.lastDistributionAt}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--ongoing_charges_pa">
                <PerformanceValue
                  label={i18n.t(
                    "pages.funds.fund_key_figures.ongoing_charges_pa",
                  )}
                  value={selectedTranche?.ongoingChargesPerc}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--income_use">
                <PerformanceValue
                  label={i18n.t("pages.funds.fund_key_figures.income_use")}
                  value={selectedFund?.distributionType}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--custodian">
                <PerformanceValue
                  label={i18n.t("pages.funds.fund_key_figures.custodian")}
                  value={selectedFund?.custodianBankName}
                />
              </li>
              <li className="funds-concept__item funds-concept__item--management_company">
                <PerformanceValue
                  label={i18n.t(
                    "pages.funds.fund_key_figures.management_company",
                  )}
                  value={selectedFund?.holdingCompanyName}
                />
              </li>
            </ul>
          </section>
        </Stack>
      </Container>
    </section>
  );
}
